import React, { useEffect, useState, useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
  FormGroup,
  Row,
  Col,
  Card,
  CardBody,
  InputGroup,
  InputGroupText,
  Container,
  Alert,
} from "reactstrap";
import { postLogin } from "../../helpers/backend_helper";
import ModernCheckInModal from "./checkinModel";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchBeats,
  fetchDivisions,
  fetchRanges,
  fetchRanges2,
  fetchRounds,
  fetchRounds2,
} from "../../store/actions";
import { eachDayOfInterval, format, parseISO } from "date-fns";
import { timeStamptoDate } from "../../helpers/formatDate";
import { CommonDatePicker } from "../../components/Common/Datepicker/CommonDatePicker";
import Datatable from "../../components/Common/Datatable/Datatable";
import { checkFilterMatch } from "../../helpers/general";

export default function UserCheckInReports() {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [checkInData, setCheckInData] = useState(null);
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 14);
    return {
      startDate: yesterday.toISOString().slice(0, 10),
      endDate: today.toISOString().slice(0, 10),
    };
  });
  
  // Add state to track applied date range
  const [appliedDateRange, setAppliedDateRange] = useState(() => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 14);
    return {
      startDate: yesterday.toISOString().slice(0, 10),
      endDate: today.toISOString().slice(0, 10),
    };
  });
  
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateError, setDateError] = useState("");

  // Separate state for form inputs and applied filters
  const [formData, setFormData] = useState({
    division: "",
    range: "",
    round: "",
    beat: "",
    assignedUser: "",
    status: "",
  });

  // Applied filters state - only updated when Apply Filters is clicked
  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    statusFilter: "all",
    division: "",
    range: "",
    round: "",
    beat: "",
  });

  const [activeTab, setActiveTab] = useState("1");
  const [statusFilter, setStatusFilter] = useState("all");
  const dispatch = useDispatch();
  const { roles, divisions, ranges, rounds, beats, error } = useSelector(
    (state) => state.Common || {}
  );

  // Memoize date columns to recalculate only when appliedDateRange changes
  const { allDates, dateColumns, totalDays } = useMemo(() => {
    // Generate all dates in the applied range
    const allDates = eachDayOfInterval({
      start: parseISO(appliedDateRange.startDate),
      end: parseISO(appliedDateRange.endDate),
    });

    const totalDays = allDates.length;

    // Helper: check if user has check-in for the given date
    const didCheckIn = (row, targetDate) => {
      const targetFormatted = format(targetDate, "yyyyMMdd");
      return row.checkIns?.some(
        (checkIn) => checkIn.DateTime === targetFormatted
      );
    };

    // Map each date to a column
    const dateColumns = allDates.map((date) => {
      const day = format(date, "dd");
      const month = format(date, "MMM");
      const dateObj = date;

      return {
        name: (
          <div style={{ textAlign: "center", lineHeight: "1.2" }}>
            <div style={{ fontSize: "12px", fontWeight: "600" }}>{day}</div>
            <div style={{ fontSize: "10px" }}>{month}</div>
          </div>
        ),
        selector: () => null,
        sortable: false,
        width: "70px",
        cell: (row) => (
          <div className="d-flex justify-content-center">
            {didCheckIn(row, dateObj) ? (
              <i
                className="ri-checkbox-circle-fill text-success"
                style={{ fontSize: "18px" }}
              />
            ) : (
              <i
                className="ri-close-circle-fill text-danger"
                style={{ fontSize: "18px" }}
              />
            )}
          </div>
        ),
      };
    });

    return { allDates, dateColumns, totalDays };
  }, [appliedDateRange]);

  // Helper functions that use the memoized values
  const getUncheckedCount = (row) => {
    const checkedDays = allDates.filter((date) => {
      const targetFormatted = format(date, "yyyyMMdd");
      return row.checkIns?.some(
        (checkIn) => checkIn.DateTime === targetFormatted
      );
    }).length;
    return totalDays - checkedDays;
  };

  const getAttendancePercentage = (row) => {
    const checkedDays = row.checkInCount || 0;
    const percentage =
      totalDays > 0 ? ((checkedDays / totalDays) * 100).toFixed(2) : "0.00";
    return percentage;
  };

 const exportToExcel = (userData) => {
    const XLSX = require("xlsx");

    // Use applied date range for export - keep as Date objects
    const exportDates = allDates.map(date => {
        // Extract date components in IST timezone
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    });

    const exportData = userData.map((user) => {
        const baseData = {
            Name: `${user.firstName} ${user.lastName}`,
            Email: user.email,
            Contact: user.contactNumber,
            Division: user.divisionName || "All",
            Range: user.rangeName || "All",
        };

        exportDates.forEach((dateString) => {
            // Create date key directly from the date string to avoid timezone issues
            const [year, month, day] = dateString.split('-');
            const dateForKey = new Date(year, month - 1, day); // month is 0-indexed in JS
            
            const dateKey = dateForKey.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
            });
            
            const hasCheckedIn = hasCheckInOnDate(user, dateString);
            baseData[dateKey] = hasCheckedIn ? "Yes" : "No";
        });

        const totalCheckIns = getCheckInCountForRange(user);
        const totalDays = exportDates.length;
        const percentage =
            totalDays > 0 ? ((totalCheckIns / totalDays) * 100).toFixed(2) : "0.00";
        baseData["Total Check-in"] = totalCheckIns;
        baseData["Unchecked"] = totalDays - totalCheckIns;
        baseData["Total Days"] = totalDays;
        baseData["Attendance %"] = `${percentage}%`;

        return baseData;
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    const colWidths = [
        { wch: 25 }, { wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 15 }
    ];

    exportDates.forEach(() => {
        colWidths.push({ wch: 12 });
    });

    colWidths.push({ wch: 15 }, { wch: 12 }, { wch: 15 });
    ws["!cols"] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, "Check-in Report");

    const formatDate = (date) => date.replace(/-/g, "");
    
    // Format timestamp in IST
    const now = new Date();
    const istTimestamp = new Date(now.toLocaleString("en-US", {timeZone: "Asia/Kolkata"}));
    const timestamp = istTimestamp.toISOString().slice(0, 19).replace(/:/g, "-");
    
    const filename = `CheckIn_Report_${formatDate(
        appliedDateRange.startDate
    )}_to_${formatDate(appliedDateRange.endDate)}_${timestamp}.xlsx`;

    XLSX.writeFile(wb, filename);
};

  const calculateDaysDifference = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const timeDifference = end.getTime() - start.getTime();
    const daysDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));
    return daysDifference + 1;
  };

  const validateDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) {
      return "Both start and end dates are required";
    }

    if (new Date(startDate) > new Date(endDate)) {
      return "Start date cannot be later than end date";
    }

    const daysDiff = calculateDaysDifference(startDate, endDate);
    if (daysDiff > 31) {
      return "Date range cannot exceed 31 days";
    }

    return "";
  };

  const columns = [
    {
      name: "User Info",
      selector: (row) => `${row.firstName} ${row.lastName}`,
      sortable: true,
      width: "250px",
      cell: (row) => (
        <div className="d-flex flex-column">
          <span
            style={{
              textTransform: "capitalize",
              fontWeight: "600",
              fontSize: "14px",
              color: "#2c3e50",
            }}
          >
            {`${row.firstName} ${row.lastName}`}
          </span>
          <span
            style={{
              fontSize: "12px",
              color: "#7f8c8d",
              marginTop: "2px",
            }}
          >
            {row.contactNumber}
          </span>
        </div>
      ),
    },
    {
      name: "Division",
      selector: (row) => row.divisionName,
      sortable: true,
      width: "120px",
      cell: (row) => (
        <span style={{ textTransform: "capitalize" }}>
          {row.divisionName || "All"}
        </span>
      ),
    },
    {
      name: "Range",
      selector: (row) => row.rangeName,
      sortable: true,
      width: "120px",
      cell: (row) => (
        <span style={{ textTransform: "capitalize" }}>
          {row.rangeName || "All"}
        </span>
      ),
    },
    ...dateColumns,
    {
      name: (
        <div style={{ textAlign: "center", lineHeight: "1.2" }}>
          <div style={{ fontSize: "12px", fontWeight: "600" }}>Total</div>
          <div style={{ fontSize: "12px" }}>Check-in</div>
        </div>
      ),
      selector: (row) => row.checkInCount,
      sortable: true,
      width: "110px",
      cell: (row) => (
        <div className="d-flex align-items-center">
          {row.checkInCount || 0}
        </div>
      ),
    },
    {
      name: (
        <div style={{ textAlign: "center", lineHeight: "1.2" }}>
          <div style={{ fontSize: "12px", fontWeight: "600" }}>Total</div>
          <div style={{ fontSize: "12px" }}>Unchecked</div>
        </div>
      ),
      selector: (row) => getUncheckedCount(row),
      sortable: true,
      width: "100px",
      cell: (row) => {
        const uncheckedCount = getUncheckedCount(row);
        return (
          <div className="d-flex align-items-center">
            {uncheckedCount}
          </div>
        );
      },
    },
    {
      name: "Percentage",
      selector: (row) => getAttendancePercentage(row),
      sortable: true,
      width: "130px",
      cell: (row) => {
        const percentage = getAttendancePercentage(row);
        return (
          <div className="d-flex align-items-center">
            {percentage}%
          </div>
        );
      },
    },
    {
      name: "Action",
      width: "90px",
      cell: (row) => (
        <Button
          color="primary"
          size="sm"
          onClick={() => handleActionClick(row)}
          style={{
            borderRadius: "6px",
            fontSize: "12px",
            padding: "4px 12px",
          }}
        >
          View
        </Button>
      ),
    },
  ];

  const handleInputChange = (e, key, fetchAction) => {
    const value = e.target.value;

    let updatedFormData = { ...formData, [key]: value };

    if (key === "division") {
      updatedFormData = {
        ...updatedFormData,
        range: "",
        round: "",
        beat: "",
      };

      if (value && value !== "0") {
        if (fetchAction) {
          dispatch(fetchAction(Number(value)));
        }
      }
    } else if (key === "range") {
      updatedFormData = {
        ...updatedFormData,
        round: "",
        beat: "",
      };

      if (value && value !== "0") {
        if (fetchAction) {
          dispatch(fetchAction(Number(value)));
        }
      }
    } else if (key === "round") {
      updatedFormData = {
        ...updatedFormData,
        beat: "",
      };
    }

    setFormData(updatedFormData);
  };

  const dependentDropdowns = [
    {
      label: "Division",
      key: "division",
      data: divisions,
      fetch: fetchRanges2,
    },
    {
      label: "Range",
      key: "range",
      data: ranges,
      fetch: fetchRounds2,
    },
    {
      label: "Round",
      key: "round",
      data: rounds,
    },
  ];

  const fetchUsers = async (
    startDate = appliedDateRange.startDate,
    endDate = appliedDateRange.endDate
  ) => {
    const validationError = validateDateRange(startDate, endDate);
    if (validationError) {
      setDateError(validationError);
      return;
    }

    setDateError("");
    setLoading(true);

    try {
      const payload = {
        act: "user",
        subact: "getCheckInsReports",
        info: {
          start: startDate.replace(/-/g, ""),
          end: endDate.replace(/-/g, ""),
        },
      };
      const response = await postLogin(payload);
      setUsers(response?.data?.user || []);
      setVehicles(response?.data?.vehicle || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(appliedDateRange.startDate, appliedDateRange.endDate);
  }, []);

  useEffect(() => {
    dispatch(fetchDivisions());

    const { division, range, round, beat } = formData;
    if (division) {
      dispatch(fetchRanges(division));

      if (range) {
        dispatch(fetchRounds(range));

        if (round) {
          dispatch(fetchBeats(round));
        }
      }
    }
  }, []);

  // MODIFIED: Apply filters function - now updates both date range and filters
  const applyFilters = () => {
    // Validate date range before applying
    const validationError = validateDateRange(dateRange.startDate, dateRange.endDate);
    if (validationError) {
      setDateError(validationError);
      return;
    }

    setDateError("");
    
    // Update applied date range - this will trigger column regeneration
    setAppliedDateRange({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });

    // Fetch users with new date range
    fetchUsers(dateRange.startDate, dateRange.endDate);

    // Update applied filters with current form values
    const newAppliedFilters = {
      search: search,
      statusFilter: statusFilter,
      division: formData.division,
      range: formData.range,
      round: formData.round,
      beat: formData.beat,
    };

    setAppliedFilters(newAppliedFilters);
  };

  const filterUsers = async (filters = appliedFilters) => {
    let filtered = users;

    if (filters.search) {
      filtered = filtered.filter(
        (u) =>
          u.firstName?.toLowerCase().includes(filters.search.toLowerCase()) ||
          u.lastName?.toLowerCase().includes(filters.search.toLowerCase()) ||
          `${u?.firstName?.trim() ?? ''} ${u?.lastName?.trim() ?? ''}`.trim().toLowerCase().includes(filters.search.toLowerCase()) ||
          u.email?.toLowerCase().includes(filters.search.toLowerCase()) ||
          u.contactNumber?.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    if (filters.statusFilter !== "all") {
      filtered = filtered.filter((u) => {
        if (filters.statusFilter === "checkin") return hasCheckInInRange(u);
        if (filters.statusFilter === "no_checkin") return !hasCheckInInRange(u);
        return true;
      });
    }
     const result = filtered.filter((user) => {
        if (!checkFilterMatch(filters.division, user.divisionId)) return false;
    
        // Range filter  
        if (!checkFilterMatch(filters.range, user.rangeId)) return false;
    
        // Round filter
        if (!checkFilterMatch(filters.round, user.roundId)) return false;
    
        // Beat filter
        if (!checkFilterMatch(filters.beat, user.beatId)) return false;
    
        return true; // Include user if all filters pass
      });


    setFilteredUsers(result);
  };

  const resetFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setFormData({
      division: "",
      range: "",
      round: "",
      beat: "",
      assignedUser: "",
      status: "",
    });

    const resetAppliedFilters = {
      search: "",
      statusFilter: "all",
      division: "",
      range: "",
      round: "",
      beat: "",
    };
    setAppliedFilters(resetAppliedFilters);

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 15);
    const newDateRange = {
      startDate: yesterday.toISOString().slice(0, 10),
      endDate: today.toISOString().slice(0, 10),
    };
    
    setDateRange(newDateRange);
    setAppliedDateRange(newDateRange); // Also update applied date range
    setDateError("");
    
    fetchUsers(newDateRange.startDate, newDateRange.endDate);
  };

  const hasCheckInOnDate = (user, date) => {
    if (!user.checkIns || user.checkIns.length === 0) return false;

    const formattedDate = date.replace(/-/g, "");
    return user.checkIns.some((checkIn) => checkIn.DateTime === formattedDate);
  };

  const hasCheckInInRange = (user) => {
    if (!user.checkIns || user.checkIns.length === 0) return false;

    const formattedStartDate = appliedDateRange.startDate.replace(/-/g, "");
    const formattedEndDate = appliedDateRange.endDate.replace(/-/g, "");

    return user.checkIns.some((checkIn) => {
      const checkInDate = checkIn.DateTime;
      return (
        checkInDate >= formattedStartDate && checkInDate <= formattedEndDate
      );
    });
  };

  const getCheckInCountForRange = (user) => {
    if (!user.checkIns || user.checkIns.length === 0) return 0;

    const formattedStartDate = appliedDateRange.startDate.replace(/-/g, "");
    const formattedEndDate = appliedDateRange.endDate.replace(/-/g, "");

    return user.checkIns.filter((checkIn) => {
      const checkInDate = checkIn.DateTime;
      return (
        checkInDate >= formattedStartDate && checkInDate <= formattedEndDate
      );
    }).length;
  };

  const handleActionClick = async (user) => {
    setSelectedUser(user);
    setModalOpen(true);
    setCheckInData(null);
  };

  const handleClose = () => {
    setModalOpen(false);
    setSelectedUser(null);
    setCheckInData(null);
  };

  const handleDateRangeChange = (field, value) => {
    const newDateRange = {
      ...dateRange,
      [field]: value,
    };

    const validationError = validateDateRange(
      newDateRange.startDate,
      newDateRange.endDate
    );

    if (validationError) {
      setDateError(validationError);
      setDateRange(newDateRange);
      return;
    }

    setDateError("");
    setDateRange(newDateRange);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  useEffect(() => {
    setFilteredUsers(users);
    filterUsers();
  }, [users]);

  useEffect(() => {
    filterUsers();
  }, [appliedFilters]);

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Control Room" breadcrumbItem="Check-Ins Report" />

        {dateError && (
          <Alert color="danger" className="mb-3">
            <i className="ri-error-warning-line me-2"></i>
            {dateError}
          </Alert>
        )}

        <Card className="shadow-sm mb-4">
          <CardBody>
            <Row className="mb-3 align-items-end">
              <Col md={2}>
                <CommonDatePicker
                  id="startDate"
                  label=" Start Date"
                  value={dateRange.startDate}
                  maxDate={new Date().toISOString().slice(0, 10)}
                  onChange={(timestamp) =>
                    handleDateRangeChange(
                      "startDate",
                      timeStamptoDate(timestamp)
                    )
                  }
                  name={" Start Date"}
                  preserveTime={true}
                />
              </Col>

              <Col md={2}>
                <CommonDatePicker
                  id="endDate"
                  label="End Date"
                  value={dateRange.endDate}
                  maxDate={new Date().toISOString().slice(0, 10)}
                  onChange={(timestamp) =>
                    handleDateRangeChange("endDate", timeStamptoDate(timestamp))
                  }
                  name={" End Date"}
                  preserveTime={true}
                />
              </Col>

              <Col md={4}>
                <Label for="search" className="fw-medium">
                  Search
                </Label>
                <InputGroup>
                  <Input
                    id="search"
                    type="text"
                    placeholder="Search by name, email, contact..."
                    value={search}
                    onChange={handleSearchChange}
                    className="form-control-modern"
                  />
                  <InputGroupText className="bg-light">
                    <i className="ri-search-line text-muted" />
                  </InputGroupText>
                </InputGroup>
              </Col>
            </Row>

            <Row className="mb-3">
              {dependentDropdowns?.map(({ label, key, data, fetch }, index) => (
                <Col md={3} key={key}>
                  <FormGroup>
                    <Label for={key} className="fw-medium">
                      {label}
                    </Label>
                    <Input
                      type="select"
                      id={key}
                      value={formData[key] || ""}
                      onChange={(e) => handleInputChange(e, key, fetch)}
                      disabled={
                        (index > 0 &&
                          !formData[
                            ["division", "range", "round"][index - 1]
                          ]) ||
                        (index > 0 && formData["division"] === "0")
                      }
                      className="form-control-modern"
                    >
                      <option value="0">All {label}s</option>
                      {Array.isArray(data?.data) &&
                        data.data.map((item) => (
                          <option
                            key={item[`${key}Id`]}
                            value={item[`${key}Id`]}
                          >
                            {item.name ||
                              item.divisionName ||
                              item.rangeName ||
                              item.beatName ||
                              item.roundName}
                          </option>
                        ))}
                    </Input>
                  </FormGroup>
                </Col>
              ))}

              <Col md={3} className="d-flex align-items-end gap-2 mb-3">
                <Button
                  color="primary"
                  onClick={applyFilters}
                  style={{ borderRadius: "6px" }}
                  disabled={loading || !!dateError}
                >
                  Apply
                </Button>
                <Button
                  color="secondary"
                  onClick={resetFilters}
                  style={{ borderRadius: "6px" }}
                  disabled={loading}
                >
                  Reset
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card className="shadow-sm">
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <p className="text-muted mb-0">
                  Showing {filteredUsers.length} of {users.length} users
                  {appliedDateRange.startDate === appliedDateRange.endDate
                    ? ` for ${appliedDateRange.startDate}`
                    : ` from ${appliedDateRange.startDate} to ${appliedDateRange.endDate}`}
                  {calculateDaysDifference(
                    appliedDateRange.startDate,
                    appliedDateRange.endDate
                  ) > 1 && (
                    <span className="badge bg-info ms-2">
                      {calculateDaysDifference(
                        appliedDateRange.startDate,
                        appliedDateRange.endDate
                      )}{" "}
                      days
                    </span>
                  )}
                </p>
              </div>
              <Button
                color="success"
                onClick={() => exportToExcel(filteredUsers)}
                style={{ borderRadius: "6px" }}
                disabled={loading || filteredUsers.length === 0}
              >
                <i className="ri-file-excel-2-line me-1"></i>
                Export Excel
              </Button>
            </div>

            <Datatable
              columns={columns}
              data={filteredUsers} 
            />
          </CardBody>
        </Card>

        <Modal isOpen={modalOpen} toggle={handleClose} size="lg" centered>
          <ModalHeader
            toggle={handleClose}
            className="bg-light border-bottom-0"
          >
            <div>
              <div style={{ fontWeight: 600, fontSize: 20, color: "#2c3e50" }}>
                {selectedUser?.firstName} {selectedUser?.lastName}
              </div>
              <div style={{ fontSize: 13, color: "#6c757d" }}>
                {selectedUser?.email} &nbsp;|&nbsp;{" "}
                {selectedUser?.contactNumber}
              </div>
            </div>
          </ModalHeader>
          <ModalBody>
            <ModernCheckInModal key={checkInData?.user} userId={selectedUser} />
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={handleClose}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
}